<script setup lang="ts">
    import {
        Dialog,
        DialogBody,
        DialogContent,
        DialogHeader,
        DialogTitle,
        DialogTrigger,
    } from '@/components/ui/dialog';
    import { toast } from 'vue-sonner';
    import { useI18n } from 'vue-i18n';
    import { ref, computed, watch } from 'vue';
    import EditButton from '@/components/EditButton.vue';
    import DialogFooterForm from '@/components/DialogFooterForm.vue';
    import { useQueryClient } from '@tanstack/vue-query';
    import CreateButton from '@/components/CreateButton.vue';
    import { ButtonVariants } from '@/components/ui/button';

    const queryClient = useQueryClient();

    const emit = defineEmits(['success', 'error', 'open', 'update:open', 'finish']);

    const { t } = useI18n();

    const isOpen = ref(false);

    const props = withDefaults(
        defineProps<{
            type: 'create' | 'update';
            form: any;
            route: string;
            queryKey: Array<string | number>;
            title: string;
            successMessage?: string;
            successMessageTranslated?: string;
            errorMessage?: string;
            method?: string;
            modalSize?: string;
            buttonText?: string;
            submitText?: string;
            footerClass?: string;
            transform?: (data: any) => any;
            disabled?: boolean;
            submitVariant?: ButtonVariants['variant'];
            hideSuccessToast?: boolean;
            hideErrorToast?: boolean;
            hideFooter?: boolean;
            open?: boolean;
            hideTrigger?: boolean;
            afterSuccess?: () => void;
        }>(),
        {
            successMessage: 'forms.general.success',
            errorMessage: 'forms.general.error',
            modalSize: 'sm:max-w-md',
            hideSuccessToast: false,
            hideErrorToast: false,
            hideFooter: false,
            hideTrigger: false,
        },
    );

    const isControlled = computed(() => props.open !== undefined);

    watch(
        () => props.open,
        (open) => {
            if (open !== undefined) {
                isOpen.value = open;
            }
        },
        { immediate: true },
    );

    const methodType = computed(() => {
        if (props.method) {
            return props.method;
        }
        return props.type === 'create' ? 'post' : 'put';
    });

    const action = () => {
        const formToSubmit = props.transform ? props.form.transform(props.transform) : props.form;

        // Capture at submit time so the callback survives an Inertia page
        // remount (e.g. when the endpoint responds with back()).
        const afterSuccess = props.afterSuccess;

        formToSubmit[methodType.value](props.route, {
            preserveScroll: true,
            onSuccess: () => {
                if (props.type === 'create') {
                    props.form.resetAndClearErrors();
                } else {
                    props.form.clearErrors();
                }
                emit('success');

                invalidateQueryAndClose();

                if (!props.hideSuccessToast) {
                    toast.success(props.successMessageTranslated || t(props.successMessage));
                }

                afterSuccess?.();
            },
            onFinish: () => {
                emit('finish');
            },
            onError: (error: any) => {
                if (!props.hideErrorToast) {
                    toast.error(error?.error || t(props.errorMessage));
                }

                emit('error', error);
            },
        });
    };

    const invalidateQueryAndClose = () => {
        queryClient.invalidateQueries({ queryKey: props.queryKey });
        isOpen.value = false;
    };

    watch(isOpen, (open) => {
        if (isControlled.value) {
            emit('update:open', open);
        }

        if (open) {
            emit('open');
            props.form.resetAndClearErrors();
        }
    });

    defineExpose({
        invalidateQueryAndClose,
    });
</script>

<template>
    <Dialog v-model:open="isOpen">
        <DialogTrigger
            v-if="!hideTrigger"
            as-child
        >
            <slot name="trigger">
                <EditButton
                    v-if="type === 'update'"
                    :disabled="disabled"
                >
                    {{ t(buttonText || 'tables.edit') }}
                </EditButton>
                <CreateButton
                    v-if="type === 'create'"
                    :button-text="buttonText"
                    :disabled="disabled"
                />
            </slot>
        </DialogTrigger>
        <DialogContent :class="modalSize">
            <form
                @submit.prevent="action"
                class="flex max-h-full flex-col overflow-hidden"
            >
                <DialogHeader>
                    <DialogTitle>{{ t(title) }}</DialogTitle>
                </DialogHeader>

                <DialogBody class="space-y-5">
                    <slot />
                </DialogBody>

                <slot name="footer">
                    <DialogFooterForm
                        :form="form"
                        :type="type"
                        @close="form.resetAndClearErrors()"
                        :class="footerClass"
                        :submit-text="submitText"
                        :submit-variant="submitVariant"
                    >
                        <slot name="footerActions" />
                    </DialogFooterForm>
                </slot>
            </form>
        </DialogContent>
    </Dialog>
</template>
